import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/shared/hooks/use-auth";
import { LoginPage } from "@/shared/ui/LoginPage";
import {
  acceptInvite,
  invalidateSpaceScopedQueries,
  invitePreviewQueryOptions,
} from "@/modules/spaces/spaces-queries";
import { useSpaceStore } from "@/modules/spaces/space-store";
import { UI_TYPE_LABEL, deriveUiType } from "@/modules/spaces/types";

export const Route = createFileRoute("/invite/$token")({
  component: InviteRoute,
});

/**
 * Приём приглашения по ссылке.
 *
 * Гость → LoginPage; useAuth перерисует роут после логина и поднимет
 * экран превью. На «Принять» — POST /api/invites/{token}, переключаем
 * активное пространство и улетаем на /.
 */
function InviteRoute() {
  const auth = useAuth();
  const { token } = Route.useParams();

  if (auth.loading) return <div className="auth-loading">Загрузка...</div>;
  if (!auth.isAuthenticated) {
    return <LoginPage onLogin={auth.login} onRegister={auth.register} />;
  }
  return <InviteAccept token={token} />;
}

function InviteAccept({ token }: { token: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setCurrentSpaceId = useSpaceStore((s) => s.setCurrentSpaceId);

  const { data, isLoading, error } = useQuery(invitePreviewQueryOptions(token));

  const acceptMutation = useMutation({
    mutationFn: () => acceptInvite(token),
    onSuccess: ({ space_id }) => {
      setCurrentSpaceId(space_id);
      invalidateSpaceScopedQueries(qc);
      qc.removeQueries({ queryKey: ["invites", "preview", token] });
      navigate({ to: "/" });
    },
  });

  // Already a member? Just flip and go home — no second click required.
  // We can't tell from the preview alone, so we let the user click; the
  // backend INSERT ON CONFLICT DO NOTHING handles the no-op gracefully.

  const goHome = () => navigate({ to: "/" });

  if (isLoading) {
    return <CenteredCard>Загрузка приглашения…</CenteredCard>;
  }

  if (error) {
    const status = (error as Error & { status?: number }).status;
    const msg =
      status === 404
        ? "Приглашение не найдено"
        : status === 410
          ? (error as Error).message
          : (error as Error).message || "Не удалось загрузить приглашение";
    return (
      <CenteredCard>
        <h1 className="font-display text-xl font-semibold text-black/90">Не получилось</h1>
        <p className="text-sm text-black/60">{msg}</p>
        <button
          type="button"
          onClick={goHome}
          className="mt-2 self-start rounded-full bg-black px-5 py-2 text-sm text-white hover:bg-black/85"
        >
          На главную
        </button>
      </CenteredCard>
    );
  }

  if (!data) return null;

  const ui = deriveUiType({
    session_visibility: data.space.session_visibility,
    access_mode: data.space.access_mode,
    member_count: 2, // any non-1 value picks a non-personal label; preview has no count
  });

  return (
    <CenteredCard>
      <div className="text-xs uppercase tracking-wide text-black/40">Приглашение в пространство</div>
      <h1 className="font-display text-2xl font-semibold text-black/90">{data.space.name}</h1>
      {data.space.description && (
        <p className="text-sm text-black/60">{data.space.description}</p>
      )}
      <div className="text-xs text-black/45">
        Тип: {UI_TYPE_LABEL[ui]} · Роль: {data.role === "owner" ? "Владелец" : "Редактор"}
      </div>

      {acceptMutation.error && (
        <div className="text-sm text-red-600">
          {(acceptMutation.error as Error).message}
        </div>
      )}

      <div className="mt-2 flex gap-2">
        <button
          type="button"
          onClick={() => acceptMutation.mutate()}
          disabled={acceptMutation.isPending}
          className="rounded-full bg-black px-5 py-2 text-sm text-white hover:bg-black/85 disabled:bg-black/30"
        >
          {acceptMutation.isPending ? "Принимаем…" : "Принять и перейти"}
        </button>
        <button
          type="button"
          onClick={goHome}
          className="rounded-full border border-black/10 px-5 py-2 text-sm hover:border-black/30"
        >
          Не сейчас
        </button>
      </div>
    </CenteredCard>
  );
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-black/5 p-6">
      <div className="flex w-full max-w-md flex-col gap-3 rounded-3xl bg-white p-8 shadow-xl">
        {children}
      </div>
    </div>
  );
}
